import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Badge } from "@/components/Base/Badge";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { Employee, IntegratorsLabel } from "@/types/v2";
import {
  findLiveEmployeeWithEmail,
  getMergeFieldChanges,
  getMergeTargets,
  isManualEmployee,
  isSyncedEmployee,
} from "@/lib/employees";
import { useStore } from "@/stores/useStore";
import { captureException } from "@/services/sentry";
import { toast } from "@/hooks/useToast";
import {
  executeMerge,
  loadMergeRolePlan,
  MergeRolePlan,
  RoleNotCopiedError,
} from "./mergeEmployees";

interface MergeEmployeeDialogProps {
  shopId: string;
  /** The manual employee that is deleted (if not already) after its data is copied. */
  manual: Employee;
  open: boolean;
  setOpen: (arg: boolean) => void;
  onMerge?: () => void;
  onComplete?: () => void;
}

const fullName = (e: Employee) => `${e.firstName} ${e.lastName}`;

const sourceLabel = (e: Employee) =>
  IntegratorsLabel[e._source as keyof typeof IntegratorsLabel] ??
  IntegratorsLabel[e._creationSource as keyof typeof IntegratorsLabel] ??
  "Synced";

const display = (value: string | null | undefined) => value || "Not set";

export const MergeEmployeeDialog = ({
  shopId,
  manual,
  open,
  setOpen,
  onMerge,
  onComplete,
}: MergeEmployeeDialogProps) => {
  const employeesIncludingDeleted = useStore(
    (state) => state.employeesIncludingDeleted
  );
  const liveEmployees = useStore((state) => state.employees);
  const shop = useStore((state) => state.shop);

  // The ref blocks a second click before React re-renders; the state is what
  // puts the button into its loading/disabled look.
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string>();
  const [plan, setPlan] = useState<MergeRolePlan>();

  const targets = useMemo(
    () => getMergeTargets(employeesIncludingDeleted, manual),
    [employeesIncludingDeleted, manual]
  );
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return targets;
    return targets.filter((e) =>
      `${fullName(e)} ${e.email}`.toLowerCase().includes(query)
    );
  }, [targets, search]);
  const synced = targets.find((e) => e.id === selectedId);

  // Start fresh each time the dialog is reopened.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedId(undefined);
      setPlan(undefined);
    }
  }, [open]);

  // Load the role plan for the chosen employee.
  useEffect(() => {
    if (!synced) return;
    let cancelled = false;
    setPlan(undefined);
    loadMergeRolePlan({ shop, manual, synced })
      .then((result) => !cancelled && setPlan(result))
      .catch((error) => {
        captureException(error);
        if (!cancelled) {
          setPlan({
            status: "skip",
            reason:
              "The role could not be checked. It will be re-checked when you merge.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // Only reload when the chosen employee changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // A deleted synced employee is restored by the merge. If a third live
  // employee already has its email, restoring would share one login.
  const conflictingEmployee =
    synced?._deleted && synced
      ? findLiveEmployeeWithEmail(
          (liveEmployees ?? []).filter((e) => e.id !== manual.id),
          synced
        )
      : undefined;

  const handleMerge = async () => {
    if (inFlight.current || conflictingEmployee || !synced) return;
    inFlight.current = true;
    setSubmitting(true);
    onMerge?.();
    try {
      const latest = useStore.getState().employeesIncludingDeleted;
      const freshManual = latest?.find((e) => e.id === manual.id);
      const freshSynced = latest?.find((e) => e.id === synced.id);
      if (
        !freshManual ||
        !freshSynced ||
        !isManualEmployee(freshManual) ||
        !isSyncedEmployee(freshSynced)
      ) {
        throw new Error("Merge participants changed before confirming");
      }
      const freshConflict = freshSynced._deleted
        ? findLiveEmployeeWithEmail(
            (useStore.getState().employees ?? []).filter(
              (e) => e.id !== manual.id && e.id !== freshSynced.id
            ),
            freshSynced
          )
        : undefined;
      if (freshConflict) {
        throw new Error("Another team member now has the synced email");
      }
      await executeMerge({
        shopId,
        shop: useStore.getState().shop,
        manual: freshManual,
        synced: freshSynced,
      });
      toast({
        title: `Merged ${fullName(freshManual)} into ${fullName(freshSynced)}`,
        variant: "success",
      });
    } catch (error) {
      if (error instanceof RoleNotCopiedError) {
        toast({
          title:
            "Merged, but the role wasn't copied. Set their role in the table.",
          variant: "default",
        });
      } else {
        captureException(error);
        toast({
          title: "Unable to merge these team members. Please try again.",
          variant: "destructive",
        });
      }
    }
    inFlight.current = false;
    setSubmitting(false);
    setOpen(false);
    onComplete?.();
  };

  const changes = synced ? getMergeFieldChanges(manual, synced) : {};

  return (
    // Dismissing mid-write would look like a cancel while the merge still
    // goes through, so ignore every close request until it finishes.
    <Dialog onOpenChange={(next) => !submitting && setOpen(next)} open={open}>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>Merge Team Member</DialogTitle>
        </DialogHeader>
        {!synced ? (
          <>
            <DialogDescription className="text-base">
              {`Choose the synced team member that ${fullName(manual)} should be merged into.`}
            </DialogDescription>
            <Input
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="py-4 text-sm text-muted">
                  No synced team members found.
                </p>
              ) : (
                filtered.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="group flex w-full items-center justify-between gap-4 rounded-md px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                    onClick={() => {
                      setPlan(undefined);
                      setSelectedId(e.id);
                    }}
                  >
                    <span>
                      <span className="font-medium">{fullName(e)}</span>{" "}
                      <span className="text-muted group-hover:text-accent-foreground group-focus-visible:text-accent-foreground">
                        {e.email}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-muted group-hover:text-accent-foreground group-focus-visible:text-accent-foreground">
                        {sourceLabel(e)}
                      </span>
                      {e._deleted && <Badge variant="secondary">Deleted</Badge>}
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <DialogDescription className="text-base">
              <span className="font-medium">Manual:</span> {fullName(manual)}
              {" · "}
              <span className="font-medium">Synced:</span> {fullName(synced)}
            </DialogDescription>
            {!plan ? (
              <p className="py-4 text-sm text-muted">Loading role details…</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    <th scope="col" />
                    <th scope="col">Manual</th>
                    <th scope="col">Synced</th>
                    <th scope="col">Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Color</th>
                    <td>{display(manual.color)}</td>
                    <td>{display(synced.color)}</td>
                    <td>
                      {changes.color
                        ? `Copied: ${changes.color}`
                        : display(synced.color)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Title</th>
                    <td>{display(manual.shopHeroJobTitle)}</td>
                    <td>{display(synced.shopHeroJobTitle)}</td>
                    <td>
                      {changes.shopHeroJobTitle
                        ? `Copied: ${changes.shopHeroJobTitle}`
                        : display(synced.shopHeroJobTitle)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Email</th>
                    <td>{manual.email}</td>
                    <td>{synced.email}</td>
                    <td>The synced email is kept</td>
                  </tr>
                  <tr>
                    <th scope="row">Role</th>
                    <td>-</td>
                    <td>-</td>
                    <td>
                      {plan.status === "copy"
                        ? `Will be set to ${plan.role}`
                        : plan.status === "link"
                          ? `Same login — keeps ${plan.role} access and links the login to ${synced.firstName}`
                          : plan.reason}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            {synced._deleted && (
              <p className="text-sm text-muted-foreground">
                {`${fullName(synced)} will be restored.`}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {manual._deleted
                ? `${fullName(manual)} is already deleted.`
                : `${fullName(manual)} will be deleted.`}
            </p>
            <p className="text-sm text-muted-foreground">
              Cards, tasks and logins are not moved.
            </p>
            {conflictingEmployee && (
              <p className="text-sm text-destructive">
                {`${fullName(conflictingEmployee)} is already a team member with the same email (${synced.email}). Merging would leave two team members sharing one login. Delete or change the email of the other team member first.`}
              </p>
            )}
          </>
        )}
        <DialogFooter>
          <div className="mt-5">
            {synced && (
              <Button
                className="mr-5 mt-5"
                variant={"secondary"}
                onClick={() => {
                  setPlan(undefined);
                  setSelectedId(undefined);
                }}
                disabled={submitting}
              >
                Back
              </Button>
            )}
            <Button
              className="mr-5 mt-5"
              variant={"secondary"}
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            {synced && (
              <Button
                className="mt-5"
                onClick={handleMerge}
                loading={submitting}
                disabled={!!conflictingEmployee || !plan}
              >
                Merge
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
